"use client";

import { useEffect, useState } from "react";
import { Activity, Camera, Compass, MapPinned, Moon, Sparkles, Telescope } from "lucide-react";

import {
    Dialog,
    DialogContent,
    DialogDescription,
    DialogFooter,
    DialogHeader,
    DialogTitle,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { useLocation } from "@/contexts/location-context";
import { FIRST_VISIT_TUTORIAL_DISMISSED_EVENT, FIRST_VISIT_TUTORIAL_SEEN_KEY } from "@/lib/first-visit-tutorial";
import { cn } from "@/lib/utils";

const dIndexBands = [
    {
        range: "0",
        label: "Quiet",
        description: "No useful aurora signal.",
        className: "bg-emerald-500/70",
    },
    {
        range: "1-2",
        label: "Watchful",
        description: "A faint glow or arc may show from dark, open-sky sites.",
        className: "bg-lime-400/80",
    },
    {
        range: "3-4",
        label: "Worth a look",
        description: "Arcs, beams, or structure are more likely if the sky is dark.",
        className: "bg-amber-400/85",
    },
    {
        range: "5+",
        label: "Strong display",
        description: "Rare, active aurora. Move fast if clouds and moon allow.",
        className: "bg-rose-500/85",
    },
];

function markTutorialSeen() {
    try {
        window.localStorage.setItem(FIRST_VISIT_TUTORIAL_SEEN_KEY, "true");
        window.dispatchEvent(new Event(FIRST_VISIT_TUTORIAL_DISMISSED_EVENT));
    } catch {
        // Private browsing or restricted storage should not block the app.
        window.dispatchEvent(new Event(FIRST_VISIT_TUTORIAL_DISMISSED_EVENT));
    }
}

function getTutorialSeen() {
    try {
        return window.localStorage.getItem(FIRST_VISIT_TUTORIAL_SEEN_KEY) === "true";
    } catch {
        return false;
    }
}

export function FirstVisitTutorialDialog() {
    const { detectedCountry, effectiveCoordinates } = useLocation();
    const [open, setOpen] = useState(false);
    const [isReady, setIsReady] = useState(false);

    const guideLatitude = detectedCountry?.lat ?? effectiveCoordinates.lat;
    const isSouthernHemisphere = guideLatitude < 0;
    const hemisphereName = detectedCountry
        ? isSouthernHemisphere
            ? "Southern Hemisphere"
            : "Northern Hemisphere"
        : "your hemisphere";
    const viewingDirection = detectedCountry
        ? isSouthernHemisphere
            ? "south"
            : "north"
        : "toward the nearest pole";
    const fieldTips = [
        {
            icon: Moon,
            title: "Darkness matters",
            description: "Check moonlight, twilight, and cloud first. A high D-index cannot beat a bright or covered sky.",
        },
        {
            icon: Compass,
            title: detectedCountry ? `Face ${viewingDirection}` : "Face the auroral horizon",
            description: detectedCountry
                ? `In the ${hemisphereName}, choose a dark site with a clear ${viewingDirection} horizon and let your eyes adapt for 15-20 minutes.`
                : "Choose a dark site with an open view toward the nearest pole and let your eyes adapt for 15-20 minutes.",
        },
        {
            icon: Camera,
            title: "Use a camera early",
            description: "Phone night mode or a long exposure can show colour before your eyes do. Stay patient if the camera sees a glow.",
        },
        {
            icon: MapPinned,
            title: "Use local reports",
            description: "Compare the map, latest submissions, and nearby observer reports before driving far.",
        },
    ];

    useEffect(() => {
        setIsReady(true);
    }, []);

    useEffect(() => {
        if (!isReady) return;
        if (!getTutorialSeen()) {
            const id = window.setTimeout(() => setOpen(true), 450);
            return () => window.clearTimeout(id);
        }
    }, [isReady]);

    const handleOpenChange = (nextOpen: boolean) => {
        setOpen(nextOpen);
        if (!nextOpen && isReady) {
            markTutorialSeen();
        }
    };

    const handleFinish = () => {
        markTutorialSeen();
        setOpen(false);
    };

    if (!isReady) return null;

    return (
        <Dialog open={open} onOpenChange={handleOpenChange}>
            <DialogContent className="flex max-h-[calc(100dvh-2rem)] flex-col gap-0 overflow-hidden border-border/80 bg-background p-0 shadow-2xl sm:max-w-[min(1120px,calc(100vw-2rem))]">
                <div className="relative flex min-h-0 flex-1 flex-col overflow-hidden">
                    <div
                        aria-hidden
                        className="absolute inset-x-0 top-0 h-48 bg-[radial-gradient(ellipse_70%_65%_at_20%_0%,color-mix(in_oklch,var(--chart-2)_20%,transparent),transparent),radial-gradient(ellipse_55%_50%_at_85%_10%,color-mix(in_oklch,var(--chart-4)_18%,transparent),transparent)]"
                    />
                    <div className="relative min-h-0 flex-1 overflow-y-auto px-5 pb-4 pt-5 lg:px-7 lg:pb-5 lg:pt-5">
                        <DialogHeader className="w-full gap-2 text-left lg:gap-2.5">
                            <div className="flex flex-wrap items-center gap-2">
                                <Badge variant="secondary" className="border-border/70 bg-background/80 text-foreground">
                                    <Sparkles className="size-3" />
                                    First-time guide
                                </Badge>
                                <Badge variant="outline" className="bg-background/70 text-muted-foreground">
                                    Built for aurora hunting
                                </Badge>
                            </div>
                            <div className="flex flex-col gap-2 lg:flex-row lg:items-start lg:gap-8">
                                <DialogTitle className="max-w-[18ch] shrink-0 text-pretty text-2xl font-semibold tracking-tight lg:text-[1.65rem]">
                                    Read the forecast like a local chaser
                                </DialogTitle>
                                <DialogDescription className="min-w-0 flex-1 text-pretty text-sm leading-5 text-muted-foreground lg:pt-1 lg:leading-6">
                                    Aurora Forecaster uses D-index to turn live space weather, location, darkness, moonlight,
                                    and local observations into a practical 0-7 signal. It is designed for the question that
                                    matters tonight: is it worth going outside?
                                </DialogDescription>
                            </div>
                        </DialogHeader>

                        <div className="mt-4 grid gap-3 lg:mt-4 lg:grid-cols-2 lg:gap-3.5">
                            <section className="rounded-xl border border-border/80 bg-card/85 p-4 text-card-foreground lg:p-3.5">
                                <div className="flex items-start gap-2.5 lg:gap-2">
                                    <div className="flex size-10 shrink-0 items-center justify-center rounded-lg bg-primary text-primary-foreground lg:size-8">
                                        <Activity className="size-5 lg:size-4" />
                                    </div>
                                    <div className="min-w-0">
                                        <h2 className="text-base font-semibold lg:text-sm">What D-index means</h2>
                                        <p className="mt-1 text-sm leading-5 text-muted-foreground lg:text-[13px] lg:leading-5">
                                            D-index describes what an observer might see, from no activity through glow,
                                            arcs, beams, curtains, and rare overhead structure.
                                        </p>
                                    </div>
                                </div>

                                <div className="mt-3 space-y-2 lg:mt-2.5 lg:space-y-1.5">
                                    {dIndexBands.map((band) => (
                                        <div key={band.range} className="grid grid-cols-[3.5rem_1fr] gap-2 lg:gap-1.5">
                                            <div className="flex items-start gap-2">
                                                <span className={cn("mt-1 size-2.5 rounded-full", band.className)} />
                                                <span className="font-mono text-sm font-semibold tabular-nums">{band.range}</span>
                                            </div>
                                            <div>
                                                <p className="text-sm font-medium">{band.label}</p>
                                                <p className="mt-0.5 text-sm leading-5 text-muted-foreground lg:text-[13px] lg:leading-4">{band.description}</p>
                                            </div>
                                        </div>
                                    ))}
                                </div>

                                <div className="mt-3 rounded-lg border border-border/70 bg-muted/40 p-2.5 lg:mt-2.5">
                                    <p className="text-sm leading-5 text-muted-foreground lg:text-[13px] lg:leading-5">
                                        D-index is preferred here because planetary three-hour averages can blur timing and
                                        local visibility. Aurora hunting is local, dark-sky, and minute-by-minute.
                                    </p>
                                </div>
                            </section>

                            <section className="rounded-xl border border-border/80 bg-card/85 p-4 text-card-foreground lg:p-3.5">
                                <div className="flex items-start gap-2.5 lg:gap-2">
                                    <div className="flex size-10 shrink-0 items-center justify-center rounded-lg bg-secondary text-secondary-foreground lg:size-8">
                                        <Telescope className="size-5 lg:size-4" />
                                    </div>
                                    <div className="min-w-0">
                                        <h2 className="text-base font-semibold lg:text-sm">Before you drive</h2>
                                        <p className="mt-1 text-sm leading-5 text-muted-foreground lg:text-[13px] lg:leading-5">
                                            Treat the number as a signal, then check the conditions that decide what you can
                                            actually see.
                                        </p>
                                    </div>
                                </div>

                                <div className="mt-3 grid gap-2.5 lg:mt-2.5 lg:gap-2">
                                    {fieldTips.map((tip) => {
                                        const Icon = tip.icon;
                                        return (
                                            <div key={tip.title} className="flex gap-3">
                                                <Icon className="mt-0.5 size-4 shrink-0 text-primary" aria-hidden />
                                                <div>
                                                    <p className="text-sm font-medium">{tip.title}</p>
                                                    <p className="mt-0.5 text-sm leading-5 text-muted-foreground lg:text-[13px] lg:leading-4">{tip.description}</p>
                                                </div>
                                            </div>
                                        );
                                    })}
                                </div>
                            </section>
                        </div>
                    </div>
                    <DialogFooter className="relative z-10 shrink-0 gap-3 border-t border-border/80 bg-background px-5 py-3.5 sm:items-center sm:justify-between sm:px-7 lg:py-3">
                        <div className="flex flex-col-reverse gap-2 sm:flex-row">
                            <Button type="button" variant="outline" onClick={handleFinish}>
                                Don&apos;t show again
                            </Button>
                            <Button type="button" onClick={handleFinish}>
                                Start watching forecast
                            </Button>
                        </div>
                    </DialogFooter>
                </div>
            </DialogContent>
        </Dialog>
    );
}
